import { ModuleInfo } from '../BinaryReader';
import { parseDwarfDebugInfo, DwarfDebugInfo, DwarfFunction, DwarfTypeInfo } from '../DwarfParser';
import { FieldResolver } from './LoweredEmitter';
import { FrameLocals } from './DwarfFrameLocals';
import { createNameResolver, sanitizeIdentifier } from './Decompiler';
import type { NameResolver } from './NameResolver';

/**
 * Binds DWARF debug info to a module's functions and exposes it as the resolvers the
 * decompiler consumes (function names, parameter types, local names, and struct-field
 * resolution). This lives in the core library — not the playground UI — so the same
 * DWARF-backed decompilation is available from the CLI and is unit-testable.
 *
 * A DWARF subprogram is matched to a wasm function by its DW_AT_low_pc, which is an
 * offset into the code section content (FunctionInfo.bodyOffset is the same space).
 */
export type { FrameLocals };

export interface DwarfDecompilation {
  nameResolver: NameResolver;
  fieldResolverFor(localFuncIndex: number): FieldResolver | undefined;
  frameLocalsFor(localFuncIndex: number): FrameLocals | undefined;
  // The parsed debug info, exposed so a whole-module assembler can emit struct typedefs and
  // global declarations from the DWARF type table and global-variable list.
  debugInfo: DwarfDebugInfo;
}

export function createDwarfDecompilation(moduleInfo: ModuleInfo, parsedDwarf?: DwarfDebugInfo): DwarfDecompilation {
  const dwarf = parsedDwarf ?? parseDwarfDebugInfo(moduleInfo.customSections);
  const boundByGlobalIndex = bindDwarfFunctions(moduleInfo, dwarf);
  const importedFuncCount = moduleInfo.imports.filter(imp => imp.kind === 0).length;

  const nameResolver = createNameResolver(
    moduleInfo,
    globalIndex => boundByGlobalIndex.get(globalIndex)?.name ?? null,
    (globalIndex, localIndex) => localNameFor(boundByGlobalIndex.get(globalIndex), localIndex),
    (globalIndex, paramIndex) => boundByGlobalIndex.get(globalIndex)?.parameters[paramIndex]?.typeName ?? null,
  );

  return {
    nameResolver,
    fieldResolverFor: (localFuncIndex: number): FieldResolver | undefined => {
      const dwarfFunc = boundByGlobalIndex.get(importedFuncCount + localFuncIndex);
      return dwarfFunc ? buildFieldResolver(dwarfFunc, dwarf.types) : undefined;
    },
    frameLocalsFor: (localFuncIndex: number): FrameLocals | undefined => {
      return buildFrameLocals(boundByGlobalIndex.get(importedFuncCount + localFuncIndex));
    },
    debugInfo: dwarf,
  };
}

/**
 * Collects the frame-slot names of one function: the frame-base wasm local plus a map from each
 * parameter/variable's frame byte offset (DW_OP_fbreg) to its sanitized name. Returns undefined
 * unless the function has a wasm-local frame base and at least one frame-resident local.
 */
function buildFrameLocals(dwarfFunc: DwarfFunction | undefined): FrameLocals | undefined {
  if (!dwarfFunc || dwarfFunc.frameBaseLocal === undefined || dwarfFunc.frameBaseLocal === null) {
    return undefined;
  }
  const slotNames = new Map<number, string>();
  for (const entry of [...dwarfFunc.parameters, ...dwarfFunc.variables]) {
    if (entry.frameOffset !== undefined && entry.frameOffset !== null && !slotNames.has(entry.frameOffset)) {
      slotNames.set(entry.frameOffset, sanitizeIdentifier(entry.name));
    }
  }
  return slotNames.size > 0 ? { frameBaseLocal: dwarfFunc.frameBaseLocal, slotNames } : undefined;
}

/**
 * Maps each defined function's global index to the DWARF subprogram whose low_pc falls
 * within that function's code byte range. Functions are laid out consecutively, so each
 * body spans [bodyOffset, next bodyOffset). When several subprograms fall in one body
 * (inlined instances), the one starting earliest is the enclosing function.
 *
 * Producers express low_pc relative to different bases (the code-section contents, or the
 * module start), so the base that binds the most functions is chosen before binding.
 */
export function bindDwarfFunctions(moduleInfo: ModuleInfo, dwarf: DwarfDebugInfo): Map<number, DwarfFunction> {
  const importedFuncCount = moduleInfo.imports.filter(imp => imp.kind === 0).length;
  const candidates = dwarf.functions.filter(dwarfFunc => dwarfFunc.name.length > 0 && dwarfFunc.lowPc > 0);
  const functions = moduleInfo.functions;
  if (functions.length === 0 || candidates.length === 0) {
    return new Map();
  }

  // bodyOffset is absolute; low_pc is relative to either the code-section contents (the
  // first body's offset) or the module start (base 0). Pick whichever lands more low_pcs.
  const bases = [functions[0].bodyOffset, 0];
  const chosenBase = bases.reduce((best, base) => (countMatches(functions, candidates, base) > countMatches(functions, candidates, best) ? base : best));

  const bound = new Map<number, DwarfFunction>();
  for (let localIndex = 0; localIndex < functions.length; localIndex++) {
    const match = matchInRange(functions, candidates, localIndex, chosenBase);
    if (match) {
      bound.set(importedFuncCount + localIndex, match);
    }
  }
  return bound;
}

function countMatches(functions: ModuleInfo['functions'], candidates: DwarfFunction[], base: number): number {
  let count = 0;
  for (let localIndex = 0; localIndex < functions.length; localIndex++) {
    if (matchInRange(functions, candidates, localIndex, base)) {
      count++;
    }
  }
  return count;
}

function matchInRange(functions: ModuleInfo['functions'], candidates: DwarfFunction[], localIndex: number, base: number): DwarfFunction | null {
  const rangeStart = functions[localIndex].bodyOffset - base;
  const rangeEnd = (localIndex + 1 < functions.length ? functions[localIndex + 1].bodyOffset : Number.MAX_SAFE_INTEGER) - base;
  let best: DwarfFunction | null = null;
  for (const candidate of candidates) {
    if (candidate.lowPc >= rangeStart && candidate.lowPc < rangeEnd) {
      if (!best || candidate.lowPc < best.lowPc) {
        best = candidate;
      }
    }
  }
  return best;
}

function localNameFor(dwarfFunc: DwarfFunction | undefined, localIndex: number): string | null {
  if (!dwarfFunc) {
    return null;
  }
  for (const param of dwarfFunc.parameters) {
    if (param.wasmLocal === localIndex) {
      return param.name;
    }
  }
  for (const variable of dwarfFunc.variables) {
    if (variable.wasmLocal === localIndex) {
      return variable.name;
    }
  }
  // Parameters occupy locals 0..n-1 in order, so when DWARF omits a parameter's explicit
  // wasm-local mapping, its position is the local index.
  const positional = dwarfFunc.parameters[localIndex];
  if (positional && positional.wasmLocal === null && positional.name) {
    return positional.name;
  }
  return null;
}

/**
 * Builds a struct-field resolver for one function: maps each pointer parameter or local
 * (by its sanitized name, matching how the decompiler names that variable) to the byte
 * offsets of the pointed-to struct's fields, so the emitter can render `point->x`.
 */
export function buildFieldResolver(dwarfFunc: DwarfFunction, types: Map<number, DwarfTypeInfo>): FieldResolver | undefined {
  if (types.size === 0) {
    return undefined;
  }
  const fieldsByVariable = new Map<string, Map<number, string>>();

  for (const param of dwarfFunc.parameters) {
    addStructFields(fieldsByVariable, param.name, param.typeOffset, types);
  }
  for (const variable of dwarfFunc.variables) {
    addStructFields(fieldsByVariable, variable.name, variable.typeOffset, types);
  }

  if (fieldsByVariable.size === 0) {
    return undefined;
  }
  return {
    resolveField(baseName: string, offset: number): string | null {
      return fieldsByVariable.get(baseName)?.get(offset) ?? null;
    },
  };
}

function addStructFields(
  fieldsByVariable: Map<string, Map<number, string>>,
  rawName: string | null,
  typeOffset: number | null,
  types: Map<number, DwarfTypeInfo>,
): void {
  if (!rawName || typeOffset === null) {
    return;
  }
  const structFields = resolvePointerToStructFields(typeOffset, types);
  if (!structFields || structFields.size === 0) {
    return;
  }
  const name = sanitizeIdentifier(rawName);
  if (!fieldsByVariable.has(name)) {
    fieldsByVariable.set(name, structFields);
  }
}

const MAX_FIELD_FLATTEN_DEPTH = 4;

/**
 * Follows pointer / const / typedef wrappers to the pointed-to struct and returns the
 * names of its fields keyed by byte offset, or null if the type is not a pointer-to-struct.
 * Embedded sub-structs are flattened with dotted names (`inner.first`) so a load at a
 * nested field's offset resolves to that nested field rather than the enclosing one.
 */
export function resolvePointerToStructFields(typeOffset: number, types: Map<number, DwarfTypeInfo>): Map<number, string> | null {
  const structType = resolvePointerToStruct(typeOffset, types);
  if (!structType) {
    return null;
  }
  const fieldMap = new Map<number, string>();
  flattenStructFields(structType, types, '', 0, 0, fieldMap);
  return fieldMap.size > 0 ? fieldMap : null;
}

function resolvePointerToStruct(typeOffset: number, types: Map<number, DwarfTypeInfo>): DwarfTypeInfo | null {
  const typeInfo = types.get(typeOffset);
  if (!typeInfo) {
    return null;
  }
  if ((typeInfo.tag === 'pointer' || typeInfo.tag === 'typedef' || typeInfo.tag === 'const') && typeInfo.referencedType !== null) {
    return resolvePointerToStruct(typeInfo.referencedType, types);
  }
  return typeInfo.tag === 'struct' && typeInfo.fields && typeInfo.fields.length > 0 ? typeInfo : null;
}

// Follows const/typedef wrappers (but NOT pointers — a pointer field's bytes are the
// pointer, not the pointed-to struct) to an embedded struct value, or null.
function embeddedStruct(typeOffset: number | null, types: Map<number, DwarfTypeInfo>): DwarfTypeInfo | null {
  if (typeOffset === null) {
    return null;
  }
  const typeInfo = types.get(typeOffset);
  if (!typeInfo) {
    return null;
  }
  if ((typeInfo.tag === 'typedef' || typeInfo.tag === 'const') && typeInfo.referencedType !== null) {
    return embeddedStruct(typeInfo.referencedType, types);
  }
  return typeInfo.tag === 'struct' && typeInfo.fields && typeInfo.fields.length > 0 ? typeInfo : null;
}

const MAX_ARRAY_ELEMENTS = 64;

function flattenStructFields(
  structType: DwarfTypeInfo,
  types: Map<number, DwarfTypeInfo>,
  prefix: string,
  baseOffset: number,
  depth: number,
  fieldMap: Map<number, string>,
): void {
  for (const field of structType.fields ?? []) {
    flattenMember(field.typeOffset, types, prefix ? `${prefix}.${field.name}` : field.name, baseOffset + field.byteOffset, depth, fieldMap);
  }
}

// Adds a struct member (or array element) to the offset->name map, recursing into
// embedded structs (`a.b`) and expanding fixed arrays (`a[0]`, `a[1]`, ...).
function flattenMember(
  typeOffset: number | null,
  types: Map<number, DwarfTypeInfo>,
  name: string,
  offset: number,
  depth: number,
  fieldMap: Map<number, string>,
): void {
  if (depth < MAX_FIELD_FLATTEN_DEPTH) {
    const nested = embeddedStruct(typeOffset, types);
    if (nested) {
      flattenStructFields(nested, types, name, offset, depth + 1, fieldMap);
      return;
    }
    const array = arrayTypeOf(typeOffset, types);
    if (array && array.referencedType !== null) {
      const elementSize = resolveByteSize(array.referencedType, types);
      const count = Math.min(array.elementCount ?? 0, MAX_ARRAY_ELEMENTS);
      if (elementSize > 0 && count > 0) {
        for (let index = 0; index < count; index++) {
          flattenMember(array.referencedType, types, `${name}[${index}]`, offset + index * elementSize, depth + 1, fieldMap);
        }
        return;
      }
    }
  }
  if (!fieldMap.has(offset)) {
    fieldMap.set(offset, name);
  }
}

function arrayTypeOf(typeOffset: number | null, types: Map<number, DwarfTypeInfo>): DwarfTypeInfo | null {
  if (typeOffset === null) {
    return null;
  }
  const typeInfo = types.get(typeOffset);
  if (!typeInfo) {
    return null;
  }
  if ((typeInfo.tag === 'typedef' || typeInfo.tag === 'const') && typeInfo.referencedType !== null) {
    return arrayTypeOf(typeInfo.referencedType, types);
  }
  return typeInfo.tag === 'array' ? typeInfo : null;
}

function resolveByteSize(typeOffset: number, types: Map<number, DwarfTypeInfo>): number {
  const typeInfo = types.get(typeOffset);
  if (!typeInfo) {
    return 0;
  }
  if (typeInfo.byteSize > 0) {
    return typeInfo.byteSize;
  }
  return typeInfo.referencedType !== null ? resolveByteSize(typeInfo.referencedType, types) : 0;
}
